import React from 'react';
import { clsx } from 'clsx';

export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: 'text' | 'circular' | 'rectangular' | 'rounded';
  width?: string | number;
  height?: string | number;
  animation?: 'pulse' | 'shimmer' | 'none';
}

export const Skeleton = React.forwardRef<HTMLDivElement, SkeletonProps>(
  (
    {
      variant = 'rectangular',
      width,
      height,
      animation = 'shimmer',
      className,
      style,
      ...props
    },
    ref
  ) => {
    const variantStyles = {
      text: 'rounded h-4',
      circular: 'rounded-full',
      rectangular: 'rounded-none',
      rounded: 'rounded-lg',
    };

    const animationStyles = {
      pulse: 'animate-pulse',
      shimmer: 'skeleton',
      none: '',
    };

    return (
      <div
        ref={ref}
        className={clsx(
          'bg-neutral-200',
          variantStyles[variant],
          animationStyles[animation],
          className
        )}
        style={{
          width: width,
          height: height,
          ...style,
        }}
        {...props}
      />
    );
  }
);

Skeleton.displayName = 'Skeleton';

// Preset skeleton components
export const SkeletonText: React.FC<{ lines?: number; className?: string }> = ({ 
  lines = 3, 
  className 
}) => {
  return (
    <div className={clsx('space-y-2', className)}>
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          variant="text"
          width={i === lines - 1 ? '60%' : '100%'}
          height={16}
        />
      ))}
    </div>
  );
};

SkeletonText.displayName = 'SkeletonText';

export const SkeletonCard: React.FC<{ className?: string }> = ({ className }) => {
  return (
    <div className={clsx('bg-white rounded-xl border border-neutral-200 p-6', className)}>
      <div className="flex items-center gap-4 mb-4">
        <Skeleton variant="circular" width={48} height={48} />
        <div className="flex-1 space-y-2">
          <Skeleton variant="text" width="50%" height={20} />
          <Skeleton variant="text" width="30%" height={16} />
        </div>
      </div>
      <SkeletonText lines={3} />
    </div>
  );
};

SkeletonCard.displayName = 'SkeletonCard';

export const SkeletonTable: React.FC<{ rows?: number; columns?: number; className?: string }> = ({
  rows = 5,
  columns = 4,
  className,
}) => {
  return (
    <div className={clsx('bg-white rounded-xl border border-neutral-200 overflow-hidden', className)}>
      {/* Header */}
      <div className="border-b border-neutral-200 bg-neutral-50 p-4">
        <div className="flex gap-4">
          {Array.from({ length: columns }).map((_, i) => (
            <Skeleton key={i} variant="text" width={`${100 / columns}%`} height={20} />
          ))}
        </div>
      </div>
      {/* Rows */}
      <div className="divide-y divide-neutral-200">
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div key={rowIndex} className="p-4 flex gap-4">
            {Array.from({ length: columns }).map((_, colIndex) => (
              <Skeleton 
                key={colIndex} 
                variant="text" 
                width={`${100 / columns}%`} 
                height={16} 
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

SkeletonTable.displayName = 'SkeletonTable';

export const SkeletonAvatar: React.FC<{ size?: 'sm' | 'md' | 'lg' | 'xl'; className?: string }> = ({
  size = 'md',
  className,
}) => {
  const sizeMap = { sm: 32, md: 40, lg: 48, xl: 64 };
  return (
    <Skeleton
      variant="circular"
      width={sizeMap[size]}
      height={sizeMap[size]}
      className={className}
    />
  );
};

SkeletonAvatar.displayName = 'SkeletonAvatar';

export const SkeletonButton: React.FC<{ width?: string | number; className?: string }> = ({
  width = 100,
  className,
}) => {
  return <Skeleton variant="rounded" width={width} height={40} className={className} />;
};

SkeletonButton.displayName = 'SkeletonButton';

// Chart skeleton for analytics pages
export const SkeletonChart: React.FC<{ height?: number; className?: string; title?: boolean }> = ({
  height = 300,
  className,
  title = true,
}) => {
  return (
    <div className={clsx('bg-white rounded-xl border border-neutral-200 p-6', className)}>
      {title && (
        <div className="mb-4">
          <Skeleton variant="text" width="30%" height={24} className="mb-2" />
          <Skeleton variant="text" width="50%" height={16} />
        </div>
      )}
      <div className="relative" style={{ height }}>
        {/* Simulated chart bars/lines */}
        <div className="absolute inset-0 flex items-end justify-around gap-2 px-4 pb-8">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton
              key={i}
              variant="rounded"
              width="100%"
              height={`${Math.random() * 60 + 40}%`}
              className="flex-1"
            />
          ))}
        </div>
        {/* X-axis labels */}
        <div className="absolute bottom-0 left-0 right-0 flex justify-around px-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Skeleton key={i} variant="text" width={40} height={12} />
          ))}
        </div>
      </div>
    </div>
  );
};

SkeletonChart.displayName = 'SkeletonChart';

// List skeleton for data lists
export const SkeletonList: React.FC<{ items?: number; className?: string; showAvatar?: boolean }> = ({
  items = 5,
  className,
  showAvatar = true,
}) => {
  return (
    <div className={clsx('space-y-3', className)}>
      {Array.from({ length: items }).map((_, i) => (
        <div
          key={i}
          className="flex items-center gap-4 p-4 bg-white rounded-lg border border-neutral-200"
        >
          {showAvatar && <SkeletonAvatar size="md" />}
          <div className="flex-1 space-y-2">
            <Skeleton variant="text" width="70%" height={18} />
            <Skeleton variant="text" width="40%" height={14} />
          </div>
          <Skeleton variant="rounded" width={80} height={32} />
        </div>
      ))}
    </div>
  );
};

SkeletonList.displayName = 'SkeletonList';

// Stat card skeleton for dashboard metrics
export const SkeletonStatCard: React.FC<{ className?: string }> = ({ className }) => {
  return (
    <div className={clsx('bg-white rounded-xl border border-neutral-200 p-6', className)}>
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <Skeleton variant="text" width="60%" height={16} className="mb-2" />
          <Skeleton variant="text" width="40%" height={32} />
        </div>
        <Skeleton variant="circular" width={48} height={48} />
      </div>
      <div className="flex items-center gap-2">
        <Skeleton variant="text" width={60} height={20} />
        <Skeleton variant="text" width={80} height={14} />
      </div>
    </div>
  );
};

SkeletonStatCard.displayName = 'SkeletonStatCard';

// Dashboard skeleton - full page loading state
export const SkeletonDashboard: React.FC<{ className?: string }> = ({ className }) => {
  return (
    <div className={clsx('space-y-6', className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <Skeleton variant="text" width={200} height={32} />
        <Skeleton variant="rounded" width={120} height={40} />
      </div>

      {/* Stat cards grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <SkeletonStatCard key={i} />
        ))}
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SkeletonChart />
        <SkeletonChart />
      </div>

      {/* Table */}
      <SkeletonTable rows={8} columns={5} />
    </div>
  );
};

SkeletonDashboard.displayName = 'SkeletonDashboard';

export default Skeleton;

